import { Radio, RadioGroup, useRadioState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from 'ui'
import { RegularScope, RegularScopeApplyTo } from '../../../../../../typings'
import { SCOPE_OPTIONS } from '../../../../../ItemsView/typing'
import { EMPTY_SCOPE } from '../constants'
import { AllItemsAlert } from './AllItemssAlert'
import { ItemsEditor } from './ItemsEditor'
import { messages } from './messages'
import { styles } from './styles'
import { scopeValidation } from './validation'
import { ItemRestriction } from 'typings'

type RegularScopeEditorProps = {
  name: string
  state: UseDrawerStateReturn
  config: RegularScope
  onSave: (config: RegularScope) => void
  selectedOption: SCOPE_OPTIONS | undefined
}

export function RegularScopeEditor(props: RegularScopeEditorProps) {
  const { name, state, config, onSave, selectedOption } = props
  const { hide } = state

  const intl = useIntl()

  const { drawerForm } = useDrawerSubForm<RegularScope>({
    config,
    isOpen: state.open,
    validationFn: scopeValidation(intl, name),
  })

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()
    if (!isValid) return

    const values = drawerForm.getValues()

    if (values.applyTo === RegularScopeApplyTo.allItems) {
      onSave({ ...EMPTY_SCOPE })
    } else {
      const filteredRestrictions = Object.values(SCOPE_OPTIONS).reduce(
        (acc, option) => {
          acc[option] = values[option]?.items.length
            ? values[option]
            : undefined
          return acc
        },
        {} as Record<SCOPE_OPTIONS, ItemRestriction | undefined>
      )

      onSave({ ...values, ...filteredRestrictions })
    }

    hide()
  }

  const applyTo: RegularScopeApplyTo = useWatch({
    form: drawerForm,
    name: 'applyTo',
  })

  const radioState = useRadioState({ orientation: 'horizontal' })

  useEffect(() => {
    if (applyTo === RegularScopeApplyTo.allItems) {
      drawerForm.clearErrors()
    }
  }, [applyTo, drawerForm])

  useEffect(() => {
    if (!!applyTo && applyTo !== radioState.value) {
      radioState.setValue(applyTo)
    }
  }, [radioState, applyTo])

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    drawerForm.setValue('applyTo', e.target.value as RegularScopeApplyTo)

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <RadioGroup
          label={null}
          state={radioState}
          onChange={handleRadioChange}
          className={styles.radioGroup}
        >
          <Radio
            value={RegularScopeApplyTo.allItems}
            label={<FormattedMessage {...messages.applyTo.allItems} />}
          />
          {applyTo === RegularScopeApplyTo.allItems && (
            <div className={styles.alert}>
              <AllItemsAlert />
            </div>
          )}
          <Radio
            value={RegularScopeApplyTo.filteredItems}
            label={<FormattedMessage {...messages.applyTo.filteredItems} />}
          />
        </RadioGroup>
        {applyTo === RegularScopeApplyTo.filteredItems && (
          <div className={styles.itemsEditor}>
            <ItemsEditor
              name={name}
              form={drawerForm}
              selectedOption={selectedOption}
            />
          </div>
        )}
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={state.hide} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
